import { AnimatedSprite, Assets, Spritesheet } from "pixi.js"
import { GRAVITY, PLAYER_FRICTION } from "./constants"
import { mod } from "./lib/mod"
import { applyVelocity } from "./lib/apply-velocity"
import { applyFriction } from "./lib/apply-friction"
import { shortestAngle } from "./lib/shortest-angle"
import { sound } from '@pixi/sound';

const PLAYER_WIDTH = 105
const PLAYER_HEIGHT = 49

const spritesheetData = {
  frames: Object.fromEntries(
    [...Array(18)].map((_, i) => [
      i,
      {
        frame: { x: i * PLAYER_WIDTH, y: 0, w: PLAYER_WIDTH, h: PLAYER_HEIGHT },
        sourceSize: { w: PLAYER_WIDTH, h: PLAYER_HEIGHT },
        spriteSourceSize: { x: 0, y: 0, w: PLAYER_WIDTH, h: PLAYER_HEIGHT },
      },
    ]),
  ),
  meta: {
    image: "/res/diver.png",
    format: "RGBA8888",
    size: { w: 1674, h: PLAYER_HEIGHT },
    scale: 1,
  },
  animations: {
    main: [...Array(18)].map((_, i) => i),
  },
}

export async function Player({ controls }) {
  const texture = await Assets.load(spritesheetData.meta.image)
  const spritesheet = new Spritesheet(texture, spritesheetData)
  await spritesheet.parse()
  const player = new AnimatedSprite(spritesheet.animations.main)
  player.play()

  // Center the sprite's anchor point
  player.anchor.set(0.8, 0.4)

  player.x = 300
  player.y = 0
  player.rotation = Math.PI / 2
  player.velocity = { x: 0, y: 0 }
  player.speed = 0.05
  player.health = 100
  player.maxHealth = 100
  player.stamina = 100
  player.maxStamina = 100
  player.expendedStamina = false
  player.lastHit = 0
  player.damage = 1
  player.nProjectiles = 1
  player.projectileSpeed = 10
  player.reloadTime = 800
  player.level = 1
  player.xp = 0

  player.levelUp = () => {
    ++player.level
    player.xp = 0
    player.openUpgrades?.()
  }

  let lastShotTime = Date.now()

  const { isKeyDown } = controls

  const tick = (time, { isSolid, spawnProjectile }) => {
    const now = Date.now()

    let playerDelta = time.deltaTime * player.speed

    const staminaRate = time.deltaTime * 0.5
    const enoughStaminaToSprint = player.stamina - staminaRate > 0
    if (
      isKeyDown("ShiftLeft") &&
      enoughStaminaToSprint &&
      (!player.expendedStamina || player.stamina >= player.maxStamina)
    ) {
      playerDelta *= 2
      player.stamina = Math.max(player.stamina - staminaRate, 0)
    } else {
      if (!enoughStaminaToSprint) player.expendedStamina = true

      player.stamina = Math.min(player.stamina + staminaRate, player.maxStamina)
      if (player.stamina >= 100) player.expendedStamina = false
    }

    // Acceleration
    let ax = 0,
      ay = 0

    if (player.health > 0) {
      if (isKeyDown("KeyW")) ay = -playerDelta
      if (isKeyDown("KeyS")) ay = playerDelta
      if (isKeyDown("KeyA")) ax = -playerDelta
      if (isKeyDown("KeyD")) ax = playerDelta
    }

    player.velocity.x += ax
    player.velocity.y += ay + GRAVITY * time.deltaTime

    applyFriction(player, { time, friction: PLAYER_FRICTION })

    player.animationSpeed =
      player.health <= 0 ? 0.1 : 0.25 + Math.sqrt(ax ** 2 + ay ** 2) * 2

    const targetRotation = Math.atan2(player.velocity.y, player.velocity.x)
    player.rotation +=
      shortestAngle(
        mod(targetRotation, 2 * Math.PI),
        mod(player.rotation, 2 * Math.PI),
      ) *
      0.05 *
      time.deltaTime
    player.scale.y = player.velocity.x !== 0 ? Math.sign(player.velocity.x) : 1

    const bulletGap = 0.08

    if (
      player.health > 0 &&
      isKeyDown("Space") &&
      now - lastShotTime >= player.reloadTime
    ) {
      sound.play('gun')
      for (let i = 0; i < player.nProjectiles; ++i) {
        const rotation = player.rotation
        const gunOffsetX = PLAYER_WIDTH * 0.22
        const gunOffsetY = player.scale.y == 1 ? 9 : -9
        const gunX =
          Math.cos(rotation) * gunOffsetX - Math.sin(rotation) * gunOffsetY
        const gunY =
          Math.sin(rotation) * gunOffsetX + Math.cos(rotation) * gunOffsetY
        spawnProjectile(
          player.x + gunX,
          player.y + gunY,
          "bullet",
          rotation -
            Math.floor(player.nProjectiles / 2) * bulletGap +
            bulletGap * i,
          player.projectileSpeed,
          player.damage,
          player.velocity,
        )
      }
      lastShotTime = Date.now()
    }

    applyVelocity(player, { isSolid, time })

    if (player.health <= 0) player.alpha = 0.4
    else if (now - player.lastHit < 500)
      player.alpha = (Math.sin(now / 100) + 1) * 0.5 * 0.6 + 0.4
    else player.alpha = 1
  }

  return {
    player,
    tick,
  }
}
